import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  HandCoins,
  Send,
  CircleDollarSign,
  Receipt,
} from 'lucide-react';
import { formatUsd } from '@/lib/rebatesData';

const Stat = ({ label, value, caption, Icon, tone = 'default', testId }) => {
  const accent = {
    default: 'text-[hsl(var(--primary))]',
    success: 'text-[hsl(var(--success))]',
    warning: 'text-[hsl(var(--warning))]',
    danger: 'text-[hsl(var(--destructive))]',
  }[tone];
  const bar = {
    default: 'bg-[hsl(var(--primary))]/60',
    success: 'bg-[hsl(var(--success))]/60',
    warning: 'bg-[hsl(var(--warning))]/60',
    danger: 'bg-[hsl(var(--destructive))]/60',
  }[tone];
  return (
    <Card
      className="relative overflow-hidden border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid={testId}
    >
      <span aria-hidden className={cn('metallic absolute left-0 top-0 h-full w-[2px]', bar)} />
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] leading-snug sm:text-sm lg:text-xs 2xl:text-sm font-bold uppercase tracking-[0.12em] break-words text-[hsl(var(--primary))]">
            {label}
          </span>
          {Icon ? <Icon size={16} className={cn('opacity-80', accent)} /> : null}
        </div>
        <div
          className="mt-1.5 text-lg sm:text-xl font-semibold tracking-tight tabular-nums text-foreground"
          data-testid={`${testId}-value`}
        >
          {value}
        </div>
        {caption ? (
          <div className="mt-1 text-xs text-muted-foreground">{caption}</div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export const RebatesStatsBar = ({ stats }) => {
  const { t } = useTranslation();
  const {
    total = 0,
    owed = 0,
    received = 0,
    count = 0,
    logged = 0,
    submitted = 0,
    loggedCount = 0,
    submittedCount = 0,
  } = stats || {};
  // "Ready to send" is the number that resets: marking a batch sent moves it
  // to "Sent — waiting". Owed (ready + waiting) only drops when money lands.
  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5"
      data-testid="rebates-stats"
    >
      <Stat
        label={t('rebateTracker.stats.totalValue')}
        value={formatUsd(total)}
        caption={t('rebateTracker.stats.eligibleReceipts', { count })}
        Icon={Receipt}
        testId="rebate-stat-total"
      />
      <Stat
        label={t('rebateTracker.stats.readyToSend')}
        value={formatUsd(logged)}
        caption={t('rebateTracker.stats.readyCaption', { count: loggedCount })}
        Icon={Send}
        tone="warning"
        testId="rebate-stat-ready"
      />
      <Stat
        label={t('rebateTracker.stats.sentWaiting')}
        value={formatUsd(submitted)}
        caption={t('rebateTracker.stats.sentCaption', {
          count: submittedCount,
          owed: formatUsd(owed),
        })}
        Icon={HandCoins}
        testId="rebate-stat-owed"
      />
      <Stat
        label={t('rebateTracker.stats.received')}
        value={formatUsd(received)}
        caption={t('rebateTracker.stats.receivedRateCaption', {
          rate: total > 0 ? Math.round((received / total) * 100) : 0,
        })}
        Icon={CircleDollarSign}
        tone="success"
        testId="rebate-stat-received"
      />
    </div>
  );
};

export default RebatesStatsBar;
